import { marked } from 'marked';

import { MENTION_TOKEN_PATTERN } from '@/helpers/assignPersonMention';

// Matches URLs (http/https/ftp and bare www.) but stops before characters that would interfere
// with markdown link syntax [text](url) or HTML attributes — specifically ( ) [ ] < > " ' and whitespace.
// This intentionally excludes URLs containing parentheses (e.g. Wikipedia-style links) to keep
// the pattern simple; those edge cases are rare in chat messages.
// Covers the same URL shapes as convertLinksToAnchors so that bare www. URLs (e.g.
// www.example.com/_internal_/docs) are also protected from the italic pass.
const URL_PATTERN = /(?:https?|ftp):\/\/[^\s<>"'()[\]]+|www\.[^\s<>"'()[\]]+/g;

// Converts markdown syntax to HTML if markdown characters are detected in the message
// Returns the original message if no markdown is found
// First preserves special tokens like {{@...}} and plain URLs to prevent markdown
// processing within them (underscores in URL paths must not become italic markers).
const convertMarkdownToHtml = (message: string): string => {
  // Store special tokens temporarily
  const tokenMap = new Map<string, string>();
  let tokenCounter = 0;

  const tokenize = (match: string): string => {
    const placeholder = `{{TEMPTOKEN${tokenCounter}}}`;
    tokenMap.set(placeholder, match);
    tokenCounter += 1;
    return placeholder;
  };

  // Replace {{@...}} mention tokens with placeholders
  const withMentionsReplaced = message.replace(MENTION_TOKEN_PATTERN, tokenize);

  // Replace plain URLs with placeholders so underscores inside URL paths (e.g.
  // /api_v2/some_path or /_internal_/) are never matched by the italic pattern below.
  // URLs inside markdown link syntax [text](url) are also covered: the URL portion is
  // tokenized to a placeholder, the markdown regex still matches [text]({{PLACEHOLDERn}}),
  // marked converts it to <a href="{{PLACEHOLDERn}}">, and the href is restored in the
  // final step.
  const withUrlsReplaced = withMentionsReplaced.replace(URL_PATTERN, tokenize);

  // Process markdown on the message with all URLs and mentions replaced by placeholders.
  // The italic pattern retains (?<!\w) / (?!\w) lookbehind/lookahead as a secondary guard
  // for non-URL underscore contexts (e.g. snake_case identifiers in chat text).
  // Lookbehind is supported in all browsers targeted by Next.js 14's default browserslist
  // (Chrome 62+, Firefox 78+, Safari 16.4+ — all 2+ years old as of 2025).
  const processedMessage = withUrlsReplaced.replace(
    /(\*\*.*?\*\*|(?<!\w)_.*?_(?!\w)|`.*?`|\[.*?\]\(.*?\))/g,
    (match: string) => {
      try {
        const parsed = marked.parseInline(match);
        return typeof parsed === 'string' ? parsed : match;
      } catch {
        return match;
      }
    },
  );

  // Restore all original tokens (mentions and URLs).
  // marked percent-encodes placeholders that appear inside href attributes (e.g.
  // [text]({{TEMPTOKEN0}}) → <a href="%7B%7BTEMPTOKEN0%7D%7D">text</a>), so we
  // restore both the raw and percent-encoded forms.
  let finalMessage = processedMessage;
  tokenMap.forEach((originalToken, placeholder) => {
    finalMessage = finalMessage.replace(placeholder, originalToken);
    const encodedPlaceholder = encodeURIComponent(placeholder);
    if (encodedPlaceholder !== placeholder) {
      finalMessage = finalMessage.replace(encodedPlaceholder, originalToken);
    }
  });

  return finalMessage;
};

export default convertMarkdownToHtml;
